import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Typography from "../../../Utility/Typography";
import ExportsFilter from "./ExportsFilter";
import ExportPreview from "./ExportPreview";
import ExportHistory from "./ExportHistory";
import {
  fetchExportsPreview,
  fetchExportsHistory,
  createBillingExport,
} from "../../../Redux/Thunks/exportsThunk";
import { fetchClientsData } from "../../../Redux/Thunks/clientsThunk";
import { fetchProjectsData } from "../../../Redux/Thunks/projectManagementThunk";
import { fetchTeamsData } from "../../../Redux/Thunks/teamsThunk";
import { fetchUsersData } from "../../../Redux/Thunks/usersThunk";
import { selectClientsData } from "../../../Redux/Slices/clientsSlice";
import { selectProjectsListForDropdown } from "../../../Redux/Slices/projectsManagementSlice";
import { selectTeamsData } from "../../../Redux/Slices/teamsSlice";
import { selectUsersData } from "../../../Redux/Slices/usersSlice";
import { getListFromApiResponse } from "../../utils/apiListMappers";
import { mapProjectsToFilterOptions } from "../../Projects/projectMappers";
import { buildUserDropdownOptionsFromUsersData } from "../../../utils/userFilterOptions";
import { buildQbReportsQueryParams } from "../../../utils/qbReportsPreview";
import {
  getBillingExportFileUrlFromResponse,
  getBillingExportMessageFromResponse,
  getBillingExportIdFromResponse,
  getBillingExportFileNameFromResponse,
  isBillingExportResponseSuccess,
} from "../../../utils/buildSaasBillingExportFilters";
import { downloadBillingExportFile } from "../../../utils/downloadBillingExportFile";
import { showSuccess, showError } from "../../../utils/toast";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { usePermissions } from "../../../hooks/usePermissions";
import PermissionDeniedModal from "../../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { PERMISSION_KEYS } from "../../../permissions/permissionKeys";
import Loader from "../../../Utility/Loader/Loader";
import { ContentLoader } from "../../../Utility/Loader";

const Exports = () => {
  const dispatch = useDispatch();
  const clientsData = useSelector(selectClientsData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const teamsData = useSelector(selectTeamsData);
  const usersData = useSelector(selectUsersData);

  const clientsListLen = getListFromApiResponse(clientsData).length;
  const projectsListLen = getListFromApiResponse(projectsData).length;
  const teamsListLen = getListFromApiResponse(teamsData).length;
  const usersListLen = getListFromApiResponse(usersData).length;

  const [filters, setFilters] = useState({
    client: [],
    project: [],
    dateRange: {
      startDate: null,
      endDate: null,
    },
    team: [],
    user: [],
  });

  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const [exportSubmitting, setExportSubmitting] = useState(false);
  const { can } = usePermissions();
  const canRunQbExport = can(PERMISSION_KEYS.QB_BILLING_EXPORT);

  const clientOptions = useMemo(() => {
    const list = getListFromApiResponse(clientsData);
    return list
      .map((c) => c.clientName ?? c.name ?? "")
      .filter(Boolean)
      .sort();
  }, [clientsData]);

  const projectOptions = useMemo(() => mapProjectsToFilterOptions(projectsData), [projectsData]);

  const teamOptions = useMemo(() => {
    const list = getListFromApiResponse(teamsData);
    return list
      .map((t) => t.teamName ?? t.name ?? "")
      .filter(Boolean)
      .sort();
  }, [teamsData]);

  const userOptions = useMemo(() => buildUserDropdownOptionsFromUsersData(usersData), [usersData]);

  const tryQbExportGenerate = (run) => {
    if (!can(PERMISSION_KEYS.QB_BILLING_EXPORT)) {
      setDeniedPermissionKey(PERMISSION_KEYS.QB_BILLING_EXPORT);
      return;
    }
    run();
  };

  const refetchQbPreview = useCallback(() => {
    const params = buildQbReportsQueryParams({
      filters,
      clientsData,
      projectsData,
      teamsData,
    });
    dispatch(fetchExportsPreview(params));
  }, [dispatch, filters, clientsData, projectsData, teamsData]);

  useEffect(() => {
    dispatch(fetchExportsHistory({ type: "qb" }));
  }, [dispatch]);

  useEffect(() => {
    if (clientsListLen === 0) dispatch(fetchClientsData({ getAll: true }));
    if (projectsListLen === 0) dispatch(fetchProjectsData({ getAll: true }));
    if (teamsListLen === 0) dispatch(fetchTeamsData({ getAll: true }));
    if (usersListLen === 0) dispatch(fetchUsersData({ getAll: true }));
  }, [dispatch, clientsListLen, projectsListLen, teamsListLen, usersListLen]);

  useEffect(() => {
    dispatch(fetchExportsPreview({}));
  }, [dispatch]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (dateRange) => {
    setFilters((prev) => ({ ...prev, dateRange }));
  };

  const handleApplyFilters = useCallback(() => {
    refetchQbPreview();
  }, [refetchQbPreview]);

  const handleAfterReset = useCallback(() => {
    dispatch(fetchExportsPreview({}));
  }, [dispatch]);

  const handleQbBillingExport = async (format) => {
    setExportSubmitting(true);
    try {
      const filterPayload = buildQbReportsQueryParams({
        filters,
        clientsData,
        projectsData,
        teamsData,
      });
      const result = await dispatch(
        createBillingExport({ format, reportType: "qb", filters: filterPayload })
      ).unwrap();
      if (!isBillingExportResponseSuccess(result)) {
        showError(getBillingExportMessageFromResponse(result) || "Export failed");
        return;
      }
      const exportId = getBillingExportIdFromResponse(result);
      const suggestedName = getBillingExportFileNameFromResponse(result);
      const url = getBillingExportFileUrlFromResponse(result);
      const msg = getBillingExportMessageFromResponse(result);
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const fallbackName = suggestedName || `export.${ext}`;

      if (exportId) {
        await downloadBillingExportFile(exportId, fallbackName);
        showSuccess(msg || "Export downloaded");
      } else if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        showSuccess(msg || "Export ready");
      } else {
        showSuccess(msg || "Export submitted");
      }
      dispatch(fetchExportsHistory({ type: "qb" }));
    } catch (e) {
      showError(typeof e === "string" ? e : getApiErrorMessage(e, "Export failed"));
    } finally {
      setExportSubmitting(false);
    }
  };

  return (
    <>
      {exportSubmitting && <Loader />}
      <ContentLoader className="min-h-[50vh]">
        <div className="w-full">
          <Typography variant="h4" className="mt-3 mb-4">
            Exports
          </Typography>

          <ExportsFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onDateRangeChange={handleDateRangeChange}
            clientOptions={clientOptions}
            projectOptions={projectOptions}
            teamOptions={teamOptions}
            userOptions={userOptions}
            applyFilters={handleApplyFilters}
            onAfterReset={handleAfterReset}
          />

          <>
            <div className="mt-6">
              <ExportPreview
                tryQbExportGenerate={tryQbExportGenerate}
                exportSubmitting={exportSubmitting}
                onExportFormat={handleQbBillingExport}
                canExportQb={canRunQbExport}
              />
            </div>
            <ExportHistory />
          </>

          <PermissionDeniedModal
            isOpen={deniedPermissionKey != null}
            onClose={() => setDeniedPermissionKey(null)}
            permissionKey={deniedPermissionKey}
          />
        </div>
      </ContentLoader>
    </>
  );
};

export default Exports;
